import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import PageLayout from "@/components/PageLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Calendar, User, Clock } from "lucide-react";
import { useBlogFeed, FeedItem } from "@/hooks/useBlogFeed";
import { useSEO } from "@/hooks/useSEO";

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const { items, loading, error, getBySlug } = useBlogFeed();
  const [imageError, setImageError] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const post: FeedItem | null = slug ? getBySlug(slug) : null;

  // Process content_html links to open in new tabs
  useEffect(() => {
    if (!contentRef.current) return;
    const links = contentRef.current.querySelectorAll("a");
    links.forEach((a) => {
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
    });
    // Hide broken images
    const imgs = contentRef.current.querySelectorAll("img");
    imgs.forEach((img) => {
      img.onerror = () => { img.style.display = "none"; };
    });
  }, [post]);

  const metaDesc = post?._seo?.meta_description || post?.summary || "";
  const metaKeywords = post?._seo?.meta_keywords?.join(", ") || post?.tags?.join(", ") || "";

  useSEO({
    title: post ? `${post.title} | Ommni` : "Blog | Ommni",
    description: metaDesc || "Read our latest articles on commerce operations and retail technology.",
    keywords: metaKeywords,
    canonical: post ? `https://ommni.io/blog/${post.slug}` : undefined,
    ogImage: post?.image || undefined,
  });

  if (loading) {
    return (
      <PageLayout>
        <div className="px-4 pb-24">
          <div className="container mx-auto max-w-3xl">
            <Skeleton className="h-8 w-32 mb-6" />
            <Skeleton className="h-80 w-full mb-8 rounded-lg" />
            <Skeleton className="h-10 w-3/4 mb-4" />
            <Skeleton className="h-6 w-1/2 mb-8" />
            <div className="space-y-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-4 w-full" />
              ))}
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!post) {
    return (
      <PageLayout>
        <div className="px-4 pb-24">
          <div className="container mx-auto max-w-3xl text-center py-16">
            <h1 className="text-4xl font-bold mb-4">Article not found</h1>
            <p className="text-muted-foreground mb-8">
              The article you're looking for doesn't exist or may have been moved.
            </p>
            <Link to="/blog">
              <Button>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to all articles
              </Button>
            </Link>
          </div>
        </div>
      </PageLayout>
    );
  }

  const authorName = post.authors?.[0]?.name || "Ommni Team";

  return (
    <PageLayout>
      {/* JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: metaDesc || post.title,
          image: post.image || "https://ommni.io/assets/ommni-logo.png",
          datePublished: post.date_published,
          dateModified: post.date_modified || post.date_published,
          author: { "@type": "Person", name: authorName },
          publisher: {
            "@type": "Organization",
            name: "Ommni",
            logo: { "@type": "ImageObject", url: "https://ommni.io/assets/ommni-logo.png" },
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": `https://ommni.io/blog/${post.slug}` },
        })}
      </script>

      <div className="px-4 pb-24">
        <article className="container mx-auto max-w-3xl">
          <Link to="/blog">
            <Button variant="ghost" className="mb-6">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to all articles
            </Button>
          </Link>

          {/* Hero Image */}
          {post.image && !imageError && (
            <div className="mb-8 -mx-4 md:mx-0 overflow-hidden rounded-lg">
              <img
                src={post.image}
                alt={post.title}
                className="w-full max-h-[400px] object-cover"
                onError={() => setImageError(true)}
              />
            </div>
          )}

          {/* Title */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
            {post.title}
          </h1>

          {/* Meta Row */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6 pb-6 border-b">
            <span className="flex items-center gap-1.5">
              <User className="h-4 w-4" />
              {authorName}
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4" />
              {formatDate(post.date_published)}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              {post.readingTime} min read
            </span>
          </div>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {post.tags.map((tag) => (
                <Badge key={tag} variant="secondary">{tag}</Badge>
              ))}
            </div>
          )}

          {/* Content */}
          <div
            ref={contentRef}
            className="blog-content text-lg"
            dangerouslySetInnerHTML={{ __html: post.content_html }}
          />
        </article>
      </div>
    </PageLayout>
  );
};

export default BlogPost;
